'use client';

/**
 * NotificationToastBridge (WEB-435)
 *
 * The SINGLE canonical mount of `useUserNotifications` for the whole app. It
 * lives at the app shell (rendered once from `app/providers.tsx`) so there is
 * exactly one `userNotifications` WebSocket subscription regardless of how many
 * surfaces (bell, panel, etc.) read notification state. WEB-433's bell
 * deliberately does NOT mount the hook — it reads the RTK Query cache that this
 * subscription keeps fresh.
 *
 * ─── INVARIANT (WEB-435) ─────────────────────────────────────────────────────
 * A live notification raises exactly ONE toast iff `payload.severity === 'high'`.
 * Normal-severity (or absent) notifications never toast — they only land in the
 * bell/panel via the cache invalidation `useUserNotifications` already performs.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * De-dupe: a high-severity toast is keyed by notification `id`. If the same
 * notification arrives twice (a live frame plus a replay on reconnect), only the
 * first raises a toast; the replay is a no-op. The seen-id set is module-scoped
 * to the component instance lifetime via a ref.
 */

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useNotification, type NotificationPriority } from '@loci/store';
import { useAuth } from '@/contexts/AuthContext';
import { useUserNotifications, type UserNotification } from '@/hooks/useUserNotifications';
import { mapHighSeverityNotificationToToast } from '@/lib/notifications/toastMapper';

declare global {
  interface Window {
    /**
     * Playwright-only bridge — set when `NEXT_PUBLIC_E2E_HOOKS=true` (WEB-695).
     * Raises a real toast through the canonical Redux queue so the toast-zone
     * spec measures the shipped surface rather than an injected stand-in.
     * `type` defaults to `info`; the WEB-1100 contrast spec raises every type.
     * `priority` defaults to `high`.
     */
    __LOCI_E2E_RAISE_TOAST__?: (
      title?: string,
      message?: string,
      type?: 'success' | 'error' | 'warning' | 'info',
      priority?: NotificationPriority,
    ) => void;
  }
}

/**
 * Bridges high-severity `userNotifications` frames to the canonical toast queue.
 *
 * Renders nothing — the visible surface is the `<ToastContainer>` already
 * mounted in `providers.tsx`. Mount this exactly once, inside the store +
 * auth providers.
 */
export function NotificationToastBridge(): null {
  const pathname = usePathname();
  const onAdmin = (pathname ?? '').startsWith('/admin');
  const { user, accessToken } = useAuth();
  const { showNotification } = useNotification();
  // De-dupe guard: ids already toasted this session. Survives re-renders.
  const toastedIdsRef = useRef<Set<string>>(new Set());

  const onNotification = useCallback(
    (payload: UserNotification) => {
      // Only high-severity frames toast. Absent severity ⇒ treat as normal.
      const toast = mapHighSeverityNotificationToToast(payload);
      if (!toast) return;

      // De-dupe by notification id so a live frame + reconnect replay of the
      // same notification raises only one toast.
      if (toastedIdsRef.current.has(payload.id)) return;
      toastedIdsRef.current.add(payload.id);

      showNotification(toast);
    },
    [showNotification],
  );

  // WEB-957 / WEB-955: root Providers keep this bridge mounted across client
  // nav. Empty userId unsubscribes; `onAdmin` is a separate hook dep so a
  // signed-out consumer (already empty userId) navigating to `/admin` still
  // disposes the cached graphql-ws client. This file never imports the
  // connection layer (WEB-372).
  useUserNotifications({
    userId: onAdmin ? '' : (user?.id ?? ''),
    accessToken: onAdmin ? null : accessToken,
    onAdmin,
    onNotification,
  });

  // WEB-695 e2e: raise a real toast without a live WS frame or a signed-in
  // session. Gated on `NEXT_PUBLIC_E2E_HOOKS` so production never exposes it.
  // Inlined at build: a build without the variable never installs this (WEB-1116).
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_E2E_HOOKS !== 'true') return;
    window.__LOCI_E2E_RAISE_TOAST__ = (title, message, type, priority) => {
      showNotification({
        type: type ?? 'info',
        title: title ?? 'Toast zone probe',
        message: message ?? 'Raised by the WEB-695 e2e bridge.',
        priority: priority ?? 'high',
        dismissible: true,
        duration: 0,
      });
    };
    return () => {
      delete window.__LOCI_E2E_RAISE_TOAST__;
    };
  }, [showNotification]);

  return null;
}
